/** Client reconnect backoff (SPEC §7.1): 0.25, 0.5, 1, 2, 4 s cap, with ±20% jitter. */
export const BACKOFF_STEPS_MS = [250, 500, 1000, 2000, 4000] as const;

export function backoffMs(attempt: number, random: () => number = Math.random): number {
  const idx = Math.min(Math.max(attempt, 0), BACKOFF_STEPS_MS.length - 1);
  const base = BACKOFF_STEPS_MS[idx] ?? 4000;
  const jitter = (random() * 2 - 1) * 0.2; // -20% … +20%
  return Math.round(base * (1 + jitter));
}

/** A connection must stay up this long before the backoff ladder starts again from the bottom. */
export const STABLE_CONNECTION_MS = 10_000;

/**
 * Backoff state for one socket. The ladder resets only after a connection was **stable**
 * (`STABLE_CONNECTION_MS`), not merely accepted: a gateway that welcomes and then drops the socket
 * (auth race, 4408 slow consumer) must not be hammered every 250 ms.
 */
export class ReconnectPolicy {
  private attempt = 0;
  private upSince: number | null = null;

  /** The session is established (Welcome received). */
  connected(nowMs: number): void {
    this.upSince = nowMs;
  }

  /** The socket closed or a connect attempt failed: delay before the next attempt. */
  nextDelayMs(nowMs: number, random: () => number = Math.random): number {
    if (this.upSince !== null && nowMs - this.upSince >= STABLE_CONNECTION_MS) this.attempt = 0;
    this.upSince = null;
    return backoffMs(this.attempt++, random);
  }

  get attempts(): number {
    return this.attempt;
  }
}
